import type { ComponentChildren } from "preact"
import { useCallback, useEffect, useState } from "preact/hooks"
import type { Plan } from "../types"
import { LoadError } from "./LoadError"
import {
	ApiError,
	fetchPlan,
	openBillingPortal,
	startCheckout,
} from "./lib/api"
import { Modal } from "./Modal"

// The price as shown in our own copy — the Pro modal, the plan section and
// the landing page (which cannot ask the server). The Dodo product is what
// actually charges, so this string must be kept equal to it by hand: change
// the price in the Dodo dashboard and change it here in the same commit.
export const PRO_PRICE = "$3 a month"

// Mirrors freeRoundsPerWindow in internal/server/plan.go. The server enforces
// the cap; this only lets copy that has no plan response yet (the landing
// page) state it.
export const FREE_ROUNDS_PER_DAY = 3

// whenPhrase says how far away an ISO time is, coarsely — the same shape as
// untilPhrase on the server ("in 3h 10m", "in 12m", "in a moment"), so the
// counter and the 402 sentence never disagree about the same moment.
export function whenPhrase(iso: string): string {
	const minutes = Math.round((Date.parse(iso) - Date.now()) / 60000)
	if (minutes < 1) return "in a moment"
	if (minutes < 60) return `in ${minutes}m`
	return `in ${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

function dateWords(iso: string): string {
	return new Date(iso).toLocaleDateString(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
	})
}

// usePlan is the one fetch for GET /api/me/plan, with the three states every
// fetched thing needs — loading, failed, loaded. reload() re-runs it; the
// profile's finishing-up state calls it on a timer.
export function usePlan() {
	const [plan, setPlan] = useState<Plan | null>(null)
	const [error, setError] = useState("")
	const reload = useCallback(() => {
		setError("")
		fetchPlan()
			.then(setPlan)
			.catch((e: unknown) =>
				setError(e instanceof Error ? e.message : "Couldn't load your plan."),
			)
	}, [])
	useEffect(reload, [reload])
	return { plan, error, reload }
}

interface UpgradeButtonProps {
	autofocus?: boolean
	// Overrides the button's resting label. PlanSection passes "Go Pro again"
	// once a player has had a subscription before (it lapsed or a payment
	// failed), so a returning Pro player never sees the first-timer's pitch.
	label?: string
}

// UpgradeButton starts a checkout and sends the browser to Dodo's hosted
// page. Whatever happens there comes back to us by webhook; the return URL
// lands on the plan section, which waits for it.
export function UpgradeButton({ autofocus, label }: UpgradeButtonProps) {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState("")
	async function go() {
		setBusy(true)
		setError("")
		try {
			const { url } = await startCheckout()
			window.location.href = url
		} catch (e) {
			setError(e instanceof Error ? e.message : "Couldn't start the checkout.")
			setBusy(false)
		}
	}
	return (
		<div class="stack gap-2 start">
			{/* autofocus is only ever set inside a just-opened modal, where Modal hands focus to it. */}
			<button
				type="button"
				class="primary lg"
				disabled={busy}
				onClick={go}
				autofocus={autofocus}
			>
				{busy ? "Opening checkout…" : (label ?? `Go Pro for ${PRO_PRICE}`)}
			</button>
			{error && <p class="text-danger small">{error}</p>}
		</div>
	)
}

// ManageButton opens Dodo's customer portal: cancel, change card, invoices.
function ManageButton() {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState("")
	async function go() {
		setBusy(true)
		setError("")
		try {
			const { url } = await openBillingPortal()
			window.location.href = url
		} catch (e) {
			setError(
				e instanceof Error ? e.message : "Couldn't open the subscription page.",
			)
			setBusy(false)
		}
	}
	return (
		<div class="stack gap-2 start">
			<button type="button" disabled={busy} onClick={go}>
				{busy ? "Opening…" : "Manage subscription"}
			</button>
			{error && <p class="text-danger small">{error}</p>}
		</div>
	)
}

// ProPitch is the two sentences that say what Pro is, shared by the modal
// and the plan section so they cannot drift.
function ProPitch() {
	return (
		<ul>
			<li>
				The free plan gives you {FREE_ROUNDS_PER_DAY} rounds a day, counting
				solo rounds and the challenges you send.
			</li>
			<li>Accepting a friend's challenge is always free.</li>
			<li>
				<strong>Pro</strong> is unlimited rounds for {PRO_PRICE}, and you can
				cancel any time.
			</li>
		</ul>
	)
}

interface ProModalProps {
	open: boolean
	onClose: () => void
	// The server's own sentence when a round was refused (the 402 body),
	// shown first so the player knows why the dialog appeared. Empty when the
	// modal was opened from the "Go Pro" link instead.
	reason?: string
}

// ProModal is the upsell: opened by Home when a round is refused (402) or
// from its "Go Pro" link. One button, one pitch, no feature table.
export function ProModal({ open, onClose, reason }: ProModalProps) {
	return (
		<Modal open={open} onClose={onClose} title="Quizfish Pro">
			<div class="stack gap-3">
				{reason && <p>{reason}</p>}
				<ProPitch />
				<UpgradeButton autofocus />
			</div>
		</Modal>
	)
}

// PlanSection is the profile's account-plan block: which tier, what is left
// of the free cap or when Pro renews or ends, and the button that changes it.
//
// It is also where the Dodo checkout returns to (/me?checkout=done). That
// redirect proves nothing — the subscription reaches us by webhook, usually
// within seconds, occasionally later — so for a short while the section says
// "finishing up" and re-reads the plan, and then says plainly that it has not
// arrived yet rather than pretending either way.
export function PlanSection({ checkoutDone }: { checkoutDone: boolean }) {
	const { plan, error, reload } = usePlan()
	// Polls while waiting for the webhook after checkout: counts down from
	// finishingPolls, stops early the moment the plan says Pro.
	const finishingPolls = 15
	const [waiting, setWaiting] = useState(checkoutDone ? finishingPolls : 0)
	useEffect(() => {
		if (waiting === 0 || plan?.tier === "pro") return
		const t = window.setTimeout(() => {
			setWaiting((n) => n - 1)
			reload()
		}, 2000)
		return () => window.clearTimeout(t)
	}, [waiting, plan, reload])

	let body: ComponentChildren
	if (error) {
		body = <LoadError message={error} onRetry={reload} />
	} else if (!plan) {
		body = <div class="spinner sm" role="status" />
	} else if (plan.tier === "pro") {
		const sub = plan.subscription
		body = (
			<div class="stack gap-2 start">
				<p>
					You're on <strong>Pro</strong>, with unlimited rounds.{" "}
					{sub?.next_billing_date && sub.status === "active"
						? sub.cancel_at_next_billing_date
							? `Your subscription is set to end on ${dateWords(sub.next_billing_date)} and won't renew.`
							: `Renews on ${dateWords(sub.next_billing_date)}.`
						: sub?.next_billing_date && sub.status === "cancelled"
							? `Your subscription is cancelled; Pro lasts until ${dateWords(sub.next_billing_date)}, the end of the period you've paid for.`
							: null}
				</p>
				{sub && <ManageButton />}
			</div>
		)
	} else if (waiting > 0) {
		body = (
			<p class="flex center gap-2">
				<span class="spinner sm" role="status" />
				Finishing up your upgrade…
			</p>
		)
	} else {
		const sub = plan.subscription
		const left = plan.rounds_left ?? 0
		body = (
			<div class="stack gap-2 start">
				{checkoutDone && (
					<p class="muted small">
						Your payment hasn't reached us yet. If you completed the checkout,
						this usually sorts itself out within a few minutes.{" "}
						<button type="button" class="link small" onClick={reload}>
							Check again
						</button>
					</p>
				)}
				{sub?.status === "on_hold" ? (
					<p>
						Your last Pro payment failed, so you're on the free plan. Update
						your card to get Pro back.
					</p>
				) : sub ? (
					<p>Your Pro subscription has ended.</p>
				) : null}
				<p>
					You're on the <strong>free plan</strong>, with {left} of{" "}
					{plan.rounds_limit} rounds left today
					{plan.resets_at && left === 0
						? `; the next one opens up ${whenPhrase(plan.resets_at)}`
						: ""}
					.
				</p>
				<ProPitch />
				<div class="flex wrap gap-2">
					{/* A lapsed or failed subscription means this player has been Pro
					    before, so the button says "again" — a plain "Go Pro" would
					    read as if they'd never signed up. */}
					<UpgradeButton label={sub ? "Go Pro again" : undefined} />
					{sub && <ManageButton />}
				</div>
			</div>
		)
	}

	return (
		<div id="plan" class="stack gap-2">
			<h2>Plan</h2>
			{body}
		</div>
	)
}

// isQuotaError is how Home tells "you've used your free rounds" from any
// other failure to start a round: the server answers 402 for exactly this.
export function isQuotaError(e: unknown): e is ApiError {
	return e instanceof ApiError && e.status === 402
}
